import { AgGridAngular } from "@ag-grid-community/angular";
import {
  CellRendererSelectorResult,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, inject, input, Input, OnInit, signal } from "@angular/core";
import {
  AccountMarketplace,
  EntityIdType,
  EntityType,
  History,
  HistoryActionEnum,
  SbCreative,
  Strategy,
  TacosStrategyGroup,
  User,
} from "@front/m19-api-client";
import { gridConfig, SIDE_BAR_NO_PIVOT } from "@front/m19-grid-config";
import { Marketplaces, SbCreativeBrandAssets, SegmentEx, SegmentTypeDec, StrategyGroupEx } from "@front/m19-models";
import {
  AccountSelectionService,
  ActivityService,
  AuthService,
  BrandAssetsService,
  EntityTypeFormats,
  HistoryValueGetter,
  SbStrategyService,
  SegmentService,
  SpStrategyService,
  StrategyCache,
  TacosStrategiesService,
} from "@front/m19-services";
import { TranslocoService } from "@jsverse/transloco";
import { LinkComponent } from "@m19-board/shared/link/link.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, map, Observable, of, Subject, switchMap, tap } from "rxjs";
import { StrategyLinkGridComponent } from "@m19-board/shared/strategy-link-grid/strategy-link-grid.component";
import { ActivityCommentComponent } from "../activity-comment/activity-comment.component";
import { DateTime } from "luxon";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivityValueComponent } from "@m19-board/activities/activity/activity-value.component";

export type ActivityFilter = {
  primaryId?: number;
  primaryType: EntityIdType;
};

@UntilDestroy()
@Component({
  selector: "app-activity-component",
  templateUrl: "./activity.component.html",
  styleUrls: ["./activity.component.scss"],
  imports: [AgGridAngular],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityComponent implements OnInit {
  private authService = inject(AuthService);
  private segmentService = inject(SegmentService);
  private sbStrategyService = inject(SbStrategyService);
  private accountSelectionService = inject(AccountSelectionService);
  private translocoService = inject(TranslocoService);
  private tacosStrategyService = inject(TacosStrategiesService);

  private readonly activityService = inject(ActivityService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly strategyCache = inject(StrategyCache);
  private readonly brandAssetService = inject(BrandAssetsService);

  private _activityFilter: ActivityFilter[] = [];
  @Input() set activityFilter(activityFilter: ActivityFilter[]) {
    this._activityFilter = activityFilter;
    this.reloadHistory$.next();
  }

  get activityFilter(): ActivityFilter[] {
    return this._activityFilter;
  }

  hideEntityColumn = input<boolean>(false);
  gridConfigStorageKey = input<string>("activities");

  // state
  user?: User;

  data = signal<History[]>([]);
  reloadHistory$: Subject<void> = new Subject();

  loading = true;

  isReadOnly = false;

  // grid configuration

  private gridOptions$: Observable<GridOptions<History>> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) =>
        combineLatest([
          this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace),
          this.segmentService.getSegments(am.accountId, am.marketplace),
          this.sbStrategyService.getSbCreativesIndex(am.accountId, am.marketplace),
          this.brandAssetService.getSbCreativeBrandAssets(am.accountId, am.marketplace),
          this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace),
          this.tacosStrategyService.getTacosStrategyIndex(am.accountId, am.marketplace),
          of(am),
        ]),
      ),
      map(
        ([strategyIndex, segmentIndex, creativeIndex, brandAssetIndex, strategyGroupIndex, tacosStrategyIndex, am]) => {
          return this.buildGridOptions(this.gridConfigStorageKey(), this.hideEntityColumn(), {
            segmentIndex: segmentIndex,
            strategyIndex: strategyIndex,
            creativeIndex: creativeIndex,
            brandAssetIndex: brandAssetIndex,
            strategyGroupIndex: strategyGroupIndex,
            tacosStrategyIndex: tacosStrategyIndex,
            accountMarketplace: am,
          });
        },
      ),
    );
  gridOptions = toSignal(this.gridOptions$);

  readonly noRowsOverlay: string =
    '<span style="padding: 10px; border: 2px solid #ccc; background: #fff;">' +
    this.translocoService.translate("activity.no_activity_selected_for_date") +
    "</span>";

  ngOnInit(): void {
    this.authService.loggedUser$.pipe(untilDestroyed(this)).subscribe((x) => (this.user = x));
    this.accountSelectionService.noAccountGroupSelected$.pipe(untilDestroyed(this)).subscribe(() => {
      this.loading = false;
    });
    this.accountSelectionService.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((b) => (this.isReadOnly = b));

    this.reloadHistory$
      .pipe(
        untilDestroyed(this),
        tap((_) => {
          this.data.set([]);
          this.loading = true;
          return _;
        }),
        switchMap(() => {
          return this.activityService.getHistory(this.activityFilter);
        }),
      )
      .subscribe((history: History[]) => {
        this.loading = false;
        this.data.set(history);
      });
    this.reloadHistory$.next();
  }

  private getPrimaryEntityValue(params: ValueGetterParams<History>, config: ActivityGridConfig) {
    switch (params.data?.entityType) {
      case EntityType.Strategy:
      case EntityType.Tactic:
      case EntityType.AudienceTargeting:
      case EntityType.SbCreative: {
        let id = params.data.primaryId;
        if (params.data?.primaryId && params.data.entityType == EntityType.SbCreative) {
          id = config.creativeIndex.get(params.data.primaryId)?.strategyId ?? 0;
        } else if (params.data.entityType == EntityType.AudienceTargeting) {
          id = JSON.parse(params.data?.newValue ?? params.data.oldValue ?? "")["strategyId"];
        }
        if (id && !config.strategyIndex.get(id)) {
          return {
            id,
            name: this.getDeletedStrategyName(id),
            component: "deletedStrategyLink",
            params: {
              strategyId: id,
            },
          };
        }
        return {
          id,
          name: config.strategyIndex.get(id!)?.name,
          component: StrategyLinkGridComponent,
          params: {
            strategyId: id,
            withCampaignType: true,
          },
        };
      }
      case EntityType.Segment: {
        if (!config.segmentIndex.has(params.data.primaryId!)) {
          return { id: params.data.primaryId, name: "Deleted Segment" };
        }
        const segment = config.segmentIndex.get(params.data.primaryId!)!;
        const name = `[${SegmentTypeDec[segment.segmentType]}] ${segment.name}`;
        return { id: params.data.primaryId, name };
      }

      case EntityType.StrategyGroup: {
        if (!params.data.primaryId || !config.strategyGroupIndex.has(params.data.primaryId!)) {
          return { id: params.data.primaryId, name: "Deleted Strategy Group" };
        }
        const name = config.strategyGroupIndex.get(params.data.primaryId)?.strategyGroupName;
        return {
          id: params.data.primaryId,
          name,
          component: LinkComponent,
          params: {
            routerLink: "/advertising/sponsored-product/strategy-group/" + params.data.primaryId,
            target: "_blank",
            content: name,
            queryParamsHandling: "merge",
          },
        };
      }

      case EntityType.TacosStrategyGroup: {
        if (!params.data.primaryId || !config.tacosStrategyIndex.has(params.data.primaryId!)) {
          return { id: params.data.primaryId, name: "Deleted Tacos Strategy" };
        }
        const relatedSp = config.strategyIndex.get(
          config.tacosStrategyIndex.get(params.data.primaryId!)!.spStrategyId!,
        );
        return {
          id: params.data.primaryId,
          name: relatedSp?.name,
          component: StrategyLinkGridComponent,
          params: {
            strategyId: params.data.primaryId,
          },
        };
      }

      case EntityType.AccountMarketplace: {
        const marketplace = Marketplaces[config.accountMarketplace!.marketplace];
        return {
          id: params.data.primaryId,
          name: `${config.accountMarketplace!.accountGroupName} ${marketplace.flag} ${config.accountMarketplace!.marketplace} - ${marketplace.name}`,
        };
      }
      default:
        return { id: params.data?.primaryId, name: params.data?.entityType + " " + params.data?.primaryId };
    }
  }

  private getSecondaryEntityValue(params: ValueGetterParams<History>, config: ActivityGridConfig) {
    switch (params.data?.entityType) {
      case EntityType.SbCreative: {
        // display creative name
        if (!params.data.primaryId || !config.brandAssetIndex.has(params.data.primaryId)) {
          return {
            id: params.data.primaryId,
            name: "Deleted Creative",
          };
        }
        return {
          id: params.data.primaryId,
          name: config.brandAssetIndex.get(params.data.primaryId)!.creativeTitle,
        };
      }
      case EntityType.Tactic: {
        if (!params.data.secondaryId || !config.segmentIndex.has(params.data.secondaryId)) {
          return { id: params.data.primaryId, name: "Deleted Segment" };
        }
        const segment = config.segmentIndex.get(params.data.secondaryId)!;
        const name = `[${SegmentTypeDec[segment.segmentType]}] ${segment.name}`;
        return { id: params.data.secondaryId, name };
      }
      default:
        return undefined;
    }
  }

  getDeletedStrategyName(primaryId: number): string {
    const strategy = this.data().find((x: History) => {
      return x.action == HistoryActionEnum.delete && x.primaryId == primaryId;
    });
    return strategy && strategy.oldValue ? JSON.parse(strategy.oldValue)["name"] + " (Deleted)" : "Deleted Strategy";
  }

  private buildGridOptions(
    localStorageKey: string,
    hideEntityColumn: boolean,
    config: ActivityGridConfig,
  ): GridOptions<History> {
    return {
      ...gridConfig({
        gridKey: localStorageKey,
        autoFitDefault: true,
      }),
      getRowId: (params: GetRowIdParams) => params.data.id,
      sideBar: SIDE_BAR_NO_PIVOT,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        useValueFormatterForExport: true,
      },
      components: {
        deletedStrategyLink: (params: any) => {
          return this.getDeletedStrategyName(params.strategyId);
        },
        text: (params: any) => {
          return params.text;
        },
      },
      columnDefs: [
        {
          headerName: this.translocoService.translate("activity.date_and_time"),
          filter: "agDateColumnFilter",
          floatingFilter: true,
          valueGetter: (params) => {
            return params.data?.timestamp ? DateTime.fromISO(params.data.timestamp, { zone: "utc" }).toJSDate() : "";
          },
          // Month spelled out: the native date filter follows the browser locale, not the board one — ref #12824
          valueFormatter: (params) =>
            params.value
              ? DateTime.fromJSDate(params.value)
                  .setLocale(this.user?.locale ?? "en-US")
                  .toLocaleString(DateTime.DATETIME_MED)
              : "",
          filterValueGetter: (params: ValueGetterParams) => {
            const d = DateTime.fromISO(params.data.timestamp, { zone: "utc" }).toJSDate();
            return new Date(d.toDateString());
          },
        },
        {
          headerName: this.translocoService.translate("activity.user"),
          field: "userName",
          floatingFilter: true,
          filter: "agMultiColumnFilter",
        },
        {
          headerName: this.translocoService.translate("activity.type"),
          field: "entityType",
          floatingFilter: true,
          valueFormatter: (params: ValueFormatterParams<History, EntityType>) =>
            this.translocoService.translate(EntityTypeFormats[params.value!]),
          filter: "agSetColumnFilter",
          filterParams: {
            valueFormatter: (params: ValueFormatterParams<History, EntityType>) =>
              this.translocoService.translate(EntityTypeFormats[params.value!]),
          },
        },
        {
          headerName: this.translocoService.translate("activity.entity"),
          hide: hideEntityColumn,
          floatingFilter: true,
          valueGetter: (params) => this.getPrimaryEntityValue(params, config),
          filter: "agMultiColumnFilter",
          valueFormatter: (
            params: ValueFormatterParams<
              History,
              {
                id: number;
                name: string;
              }
            >,
          ) => params.value?.name ?? "",
          filterParams: {
            filters: [
              // TODO: add a agTextFilter
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  keyCreator: (params: ValueFormatterParams<History, { id: number; name: string }>) => params.value?.id,
                  valueFormatter: (params: ValueFormatterParams<History, { id: number; name: string }>) =>
                    params.value?.name,
                },
              },
            ],
          },
          cellRendererSelector: (params) => {
            if (params.value.component) {
              return {
                component: params.value.component,
                params: params.value.params,
              };
            }
            return {
              component: "text",
              params: { text: params.value.name },
            };
          },
        },
        {
          headerName: this.translocoService.translate("activity.sub_type"),
          floatingFilter: true,
          valueGetter: (params) => this.getSecondaryEntityValue(params, config),
          filter: "agMultiColumnFilter",
          valueFormatter: (
            params: ValueFormatterParams<
              History,
              {
                id: number;
                name: string;
              }
            >,
          ) => params.value?.name ?? "",
          filterParams: {
            filters: [
              // TODO: add a agTextFilter
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  keyCreator: (params: ValueFormatterParams<History, { id: number; name: string }>) => params.value?.id,
                  valueFormatter: (params: ValueFormatterParams<History, { id: number; name: string }>) =>
                    params.value?.name,
                },
              },
            ],
          },
          cellRendererSelector: (params) => {
            if (!params.value) {
              return undefined;
            }
            if (params.value.component) {
              return {
                component: params.value.component,
                params: params.value.params,
              };
            }
            return {
              component: "text",
              params: { text: params.value.name },
            };
          },
        },
        {
          headerName: this.translocoService.translate("activity.property_changed"),
          floatingFilter: true,
          field: "property",
          valueFormatter: (params) => {
            const s = this.activityService.formatHistoryProperty(params!.data!);
            return this.translocoService.translate(s);
          },
          filterValueGetter: (params) =>
            this.translocoService.translate(this.activityService.formatHistoryProperty(params!.data!)),
          filter: "agSetColumnFilter",
        },
        {
          headerName: this.translocoService.translate("activity.old_value"),
          field: "oldValue",
          cellRendererSelector: (params: ICellRendererParams<History>) =>
            this.getHistoryValueRenderer(params, "oldValue"),
        },
        {
          headerName: this.translocoService.translate("activity.new_value"),
          field: "newValue",
          cellRendererSelector: (params: ICellRendererParams<History>) =>
            this.getHistoryValueRenderer(params, "newValue"),
        },
        {
          headerName: this.translocoService.translate("activity.comments"),
          field: "comment",
          cellRendererSelector: (params: ICellRendererParams) => ({
            component: ActivityCommentComponent,
            params: {
              readonly: this.isReadOnly,
              commentUpdate: (comment: string) => {
                params.data.comment = comment;
              },
            },
          }),
        },
      ],
    };
  }

  private getHistoryValueRenderer(
    params: ICellRendererParams<History>,
    valGetter: HistoryValueGetter,
  ): CellRendererSelectorResult {
    return {
      component: ActivityValueComponent,
      params: {
        value: this.activityService.formatHistoryValue(params!.data!, valGetter, this.user?.locale ?? ""),
      },
    };
  }
}

type ActivityGridConfig = {
  segmentIndex: Map<number, SegmentEx>;
  strategyIndex: Map<number, Strategy>;
  creativeIndex: Map<number, SbCreative>;
  brandAssetIndex: Map<number, SbCreativeBrandAssets>;
  strategyGroupIndex: Map<number, StrategyGroupEx>;
  tacosStrategyIndex: Map<number, TacosStrategyGroup>;
  accountMarketplace: AccountMarketplace;
};
